import { useCallback } from 'react';
import {
  useQuery,
  useSuspenseQuery,
  QueryResult,
  useLazyQuery,
  useApolloClient,
  QueryOptions,
  QueryHookOptions,
  SuspenseQueryHookOptions,
  LazyQueryHookOptions,
  LazyQueryExecFunction
} from '@apollo/client';
import { useSnackbarHelper } from '../../useSnackbarHelper';
import QUERY from '../operations/AllPayments.graphql';
import { Query, QueryallPaymentsArgs } from '../../__generated__/globalTypes';

export const useAllPaymentsQuery = (options?: QueryHookOptions<Query, QueryallPaymentsArgs>) => {
  const { t, enqueueSnackbar } = useSnackbarHelper();
  const result = useQuery<Query, QueryallPaymentsArgs>(QUERY, {
    onError: (error) => {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    },
    ...options
  });

  return result;
};

export const useAllPaymentsLazyQuery = (
  options?: LazyQueryHookOptions<Query, QueryallPaymentsArgs>
): QueryResult<Query, QueryallPaymentsArgs> & {
  getAllPayments: LazyQueryExecFunction<Query, QueryallPaymentsArgs>;
} => {
  const { t, enqueueSnackbar } = useSnackbarHelper();
  const [getAllPayments, result] = useLazyQuery<Query, QueryallPaymentsArgs>(QUERY, {
    onError: (error) => {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    },
    ...options
  });

  return { getAllPayments, ...result };
};

export const useAllPaymentsPromiseQuery = () => {
  const client = useApolloClient();

  const getAllPayments = useCallback(
    (options?: Omit<QueryOptions<QueryallPaymentsArgs, Query>, 'query'>) =>
      client.query<Query, QueryallPaymentsArgs>({ ...options, query: QUERY }),
    [client]
  );

  return { getAllPayments };
};
// Suspense Query Hooks
export const useAllPaymentsSuspenseQuery = (options?: SuspenseQueryHookOptions<Query, QueryallPaymentsArgs>) => {
  const result = useSuspenseQuery<Query, QueryallPaymentsArgs>(QUERY, {
    ...options
  });

  return result;
};
